import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import DisclosureBar from "../components/site/DisclosureBar";
import EmailOptin from "../components/site/EmailOptin";
import { FadeUp, MaskedLines, Stagger, staggerItem } from "../components/site/Reveal";
import { GUIDES, getGuide } from "../data/guides";
import { getPlatform } from "../data/platforms";
import { motion } from "framer-motion";

export const GuidesIndexPage = () => {
  useEffect(() => { document.title = "Email Marketing Guides (2026) | Top Email Marketing Tools"; }, []);
  return (
    <main className="mx-auto max-w-6xl px-6 pt-40 pb-28" data-testid="guides-index">
      <p className="overline-label mb-5">Field Guides</p>
      <h1 className="max-w-3xl font-display text-4xl font-black tracking-tighter md:text-5xl">
        <MaskedLines lines={["Learn email marketing"]} />
        <MaskedLines delay={0.15} lines={["the honest way"]} lineClassName="font-serif-accent font-medium italic text-volt" />
      </h1>
      <FadeUp delay={0.3}>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/60">Six practical guides covering the craft end to end — from your first campaign to fixing deliverability. No filler, no gated content.</p>
      </FadeUp>
      <Stagger className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {GUIDES.map((g, i) => (
          <motion.div key={g.slug} variants={staggerItem}>
            <Link to={`/guides/${g.slug}`} data-testid={`guide-card-${g.slug}`} className="group flex h-full flex-col rounded-2xl border border-white/[0.07] bg-cardink p-8 transition-transform duration-300 hover:-translate-y-1.5">
              <div className="flex items-center justify-between">
                <span className="font-serif-accent text-4xl italic text-volt/40">{String(i + 1).padStart(2, "0")}</span>
                <span className="flex items-center gap-1.5 text-xs text-white/40"><Clock className="h-3 w-3" />{g.readTime}</span>
              </div>
              <p className="overline-label mt-5 !text-[10px]">{g.overline}</p>
              <h2 className="mt-2 flex-1 font-display text-xl font-bold leading-snug tracking-tight transition-colors group-hover:text-volt">{g.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-white/50">{g.lede.split(".")[0]}.</p>
            </Link>
          </motion.div>
        ))}
      </Stagger>
    </main>
  );
};

const GuidePage = () => {
  const { slug } = useParams();
  const guide = getGuide(slug);

  useEffect(() => {
    if (guide) document.title = `${guide.title} | Top Email Marketing Tools`;
    window.scrollTo(0, 0);
  }, [slug, guide]);

  if (!guide) {
    return (
      <main className="mx-auto max-w-3xl px-6 pt-48 pb-32 text-center" data-testid="guide-not-found">
        <h1 className="font-display text-4xl font-black">Guide not found</h1>
        <Link to="/guides" className="mt-6 inline-block text-volt underline">All guides</Link>
      </main>
    );
  }

  return (
    <main>
      <div className="pt-[76px]"><DisclosureBar /></div>
      <header className="mx-auto max-w-6xl px-6 pt-16 pb-10">
        <Link to="/guides" data-testid="guide-back" className="overline-label transition-colors hover:text-volt">← All Guides</Link>
        <p className="overline-label mb-5 mt-8" data-testid="guide-overline">{guide.overline} · {guide.readTime}</p>
        <h1 className="max-w-3xl font-display text-4xl font-black leading-[1.08] tracking-tighter md:text-5xl" data-testid="guide-title">
          <MaskedLines lines={[guide.title]} />
        </h1>
        <FadeUp delay={0.25}>
          <p className="mt-6 max-w-2xl font-serif-accent text-xl italic leading-relaxed text-white/60">{guide.lede}</p>
        </FadeUp>
      </header>

      <div className="mx-auto grid max-w-6xl gap-14 px-6 pb-28 lg:grid-cols-[280px_1fr]">
        <aside className="hidden lg:block">
          <div className="glass sticky top-28 rounded-2xl p-6" data-testid="guide-toc">
            <p className="overline-label mb-4">Contents</p>
            <ul className="space-y-1">
              {guide.sections.map((s, i) => (
                <li key={i}>
                  <a href={`#g-${i}`} data-testid={`guide-toc-${i}`} className="block rounded-lg px-3 py-2 text-sm text-white/55 transition-colors hover:bg-white/5 hover:text-volt">
                    <span className="mr-2 font-serif-accent italic text-white/30">{String(i + 1).padStart(2, "0")}</span>{s.h}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <article className="min-w-0 max-w-3xl space-y-14" data-testid="guide-article">
          {guide.sections.map((s, i) => (
            <section key={i} id={`g-${i}`} className="scroll-mt-28 border-t border-white/[0.07] pt-10" data-testid={`guide-section-${i}`}>
              <FadeUp>
                <div className="flex items-baseline gap-4">
                  <span className="font-serif-accent text-3xl italic text-volt/50">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-2xl font-black tracking-tight">{s.h}</h2>
                </div>
              </FadeUp>
              <div className="mt-6">
                {s.body.map((p, j) => <p key={j} className="mb-5 text-base leading-[1.85] text-white/65">{p}</p>)}
                {s.list && (
                  <ul className="my-6 space-y-3">
                    {s.list.map((li, j) => (
                      <li key={j} className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-cardink px-5 py-4 text-sm leading-relaxed text-white/75">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-volt" />{li}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          ))}

          <FadeUp className="rounded-2xl border border-volt/25 bg-cardink p-8" data-testid="guide-cta">
            <p className="overline-label mb-3 !text-volt">Put It Into Practice</p>
            <h3 className="font-display text-xl font-bold">The platforms we recommend for this</h3>
            <div className="mt-5 space-y-4">
              {guide.featured.map((fslug) => {
                const p = getPlatform(fslug);
                if (!p) return null;
                return (
                  <div key={fslug} className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-white/[0.07] bg-surface p-5">
                    <div className="flex items-center gap-4">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg font-display text-xs font-extrabold" style={{ backgroundColor: `${p.hue}1f`, color: p.hue }}>{p.monogram}</span>
                      <div>
                        <p className="font-display font-bold">{p.name} <span className="ml-1 text-xs font-normal text-volt">{p.badge}</span></p>
                        <p className="text-xs text-white/45">From {p.startingPrice}/mo · {p.freePlan}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <a href={p.affiliateUrl} target="_blank" rel="noopener noreferrer sponsored" data-testid={`guide-cta-${fslug}`} className="flex items-center gap-1.5 rounded-full bg-volt px-5 py-2.5 text-xs font-bold text-ink transition-colors hover:bg-volt-hover">
                        Try Free <ArrowUpRight className="h-3.5 w-3.5" />
                      </a>
                      <Link to={`/reviews/${fslug}`} data-testid={`guide-review-${fslug}`} className="text-xs text-white/50 underline-offset-2 hover:text-volt hover:underline">Review</Link>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 border-t border-white/[0.07] pt-6">
              <p className="mb-4 text-sm text-white/55">Get the 10-point checklist that pairs with this guide:</p>
              <EmailOptin source={`guide-${slug}`} compact />
            </div>
          </FadeUp>
        </article>
      </div>
    </main>
  );
};

export default GuidePage;
